import { FC } from 'react';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { useShallow } from 'zustand/react/shallow';

import { type ISetPaymentMethodParams } from '@mopla/business-logic';
import { BlackOverlay } from '@mopla/ui';

import { usePaymentStepStore } from '../../stores/usePaymentStepStore';
import { PaymentSelectSepaOrStripe } from '../PaymentSelectSepaOrStripe';
import { Dialog } from '../PaymentSelectSepaOrStripe/PaymentSelectSepaOrStripe.styles';
import { SepaRedirectInfoStep } from '../PaymentSelectSepaOrStripe/SepaRedirectInfoStep';
import { SepaPayment } from '../SepaPayment';
import { StripePayment } from '../StripePayment';

export interface IPaymentProps {
	onCancel: VoidFunction;
	onDismiss?: VoidFunction;
	stripeClientSecret?: string;
	stripeElementOptions?: StripeElementsOptions;
	onStripeCardFlowCompleted: (options: ISetPaymentMethodParams) => void;
	onStripeFlowClose: VoidFunction;
	stripeError?: string;
}

export const Payment: FC<IPaymentProps> = ({
	onCancel,
	onDismiss,
	stripeClientSecret,
	stripeError,
	onStripeCardFlowCompleted,
	onStripeFlowClose,
}) => {
	const { step, setPaymentStep } = usePaymentStepStore(
		useShallow((state) => ({
			step: state.step,
			setPaymentStep: state.setStep,
		}))
	);

	switch (step) {
		case 'sepaCompletion':
			return <SepaPayment onCancel={onCancel} />;
		case 'sepaRedirect':
			return (
				<>
					<BlackOverlay open onClick={onDismiss || onCancel} />
					<Dialog
						sx={{
							borderRadius: '16px',
							position: 'fixed',
							zIndex: 1300,
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
						}}
					>
						<SepaRedirectInfoStep />
					</Dialog>
				</>
			);
		case 'stripe':
			return (
				<StripePayment
					stripeClientSecret={stripeClientSecret}
					onCardFlowCompleted={onStripeCardFlowCompleted}
					onClose={onStripeFlowClose}
					_error={stripeError}
				/>
			);
		case 'sepaAuthentication':
		case 'selection':
			return (
				<PaymentSelectSepaOrStripe
					handleOnChange={setPaymentStep}
					handleOnClose={onCancel}
				/>
			);
		default:
			return (
				<StripePayment
					stripeClientSecret={stripeClientSecret}
					onCardFlowCompleted={onStripeCardFlowCompleted}
					onClose={onStripeFlowClose}
					_error={stripeError}
				/>
			);
	}
};
